import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { DollarSign, TrendingUp, Download, Calendar, ArrowUpRight, X } from 'lucide-react';
import { toast } from 'sonner';
import AgentLayout from '../../components/layout/AgentLayout';
import { formatApiErrorDetail } from '../../contexts/AuthContext';

export const AgentCommissionPage = () => {
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const [loading, setLoading] = useState(true);
  const [commissionWallet, setCommissionWallet] = useState(null);
  const [commissionHistory, setCommissionHistory] = useState([]);
  const [withdrawing, setWithdrawing] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawNote, setWithdrawNote] = useState('');
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [stats, setStats] = useState({
    deposits: { count: 0, commission: 0 },
    withdrawals: { count: 0, commission: 0 },
    total: 0
  });

  useEffect(() => {
    fetchCommissionData();
  }, []);

  const fetchCommissionData = async () => {
    setLoading(true);
    try {
      // Fetch agent profile with commission wallet
      const profileRes = await fetch(`${API_URL}/api/agents/me/profile`, {
        credentials: 'include'
      });

      if (profileRes.ok) {
        const profile = await profileRes.json();
        setCommissionWallet(profile.commission_wallet || { balance: 0, total_earned: 0 });
      }

      // Fetch commission history
      const historyRes = await fetch(`${API_URL}/api/agents/me/commission-history`, {
        credentials: 'include'
      });

      if (historyRes.ok) {
        const data = await historyRes.json();
        setCommissionHistory(data.history || []);
        calculateStats(data.history || []);
      }
    } catch (error) {
      console.error('Failed to fetch commission data:', error);
      toast.error('Failed to load commission data');
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (history) => {
    const depositCommissions = history.filter(h => h.source === 'deposit');
    const withdrawalCommissions = history.filter(h => h.source === 'withdrawal');

    setStats({
      deposits: {
        count: depositCommissions.length,
        commission: depositCommissions.reduce((sum, h) => sum + (h.amount || 0), 0)
      },
      withdrawals: {
        count: withdrawalCommissions.length,
        commission: withdrawalCommissions.reduce((sum, h) => sum + (h.amount || 0), 0)
      },
      total: history.reduce((sum, h) => sum + (h.amount || 0), 0)
    });
  };

  const exportHistory = () => {
    const csv = [
      ['Date', 'Source', 'Amount', 'Reference', 'Balance After'].join(','),
      ...commissionHistory.map(h => [
        new Date(h.timestamp).toLocaleDateString(),
        h.source,
        h.amount?.toFixed(2),
        h.reference,
        h.balance_after?.toFixed(2)
      ].join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `commission-history-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    toast.success('Commission history exported!');
  };

  const handleWithdrawRequest = async (e) => {
    e.preventDefault();
    const amount = parseFloat(withdrawAmount);
    
    if (!amount || amount <= 0) {
      return toast.error('Enter a valid amount');
    }
    
    if (amount > (commissionWallet?.balance || 0)) {
      return toast.error('Insufficient commission balance');
    }
    
    setWithdrawing(true);
    try {
      const response = await fetch(`${API_URL}/api/agents/me/commission/withdraw-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          amount,
          note: withdrawNote.trim()
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Withdrawal request failed');
      }
      
      toast.success('Withdrawal request submitted! Admin will review and process.');
      setShowWithdrawModal(false);
      setWithdrawAmount('');
      setWithdrawNote('');
      fetchCommissionData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setWithdrawing(false);
    }
  };

  return (
    <AgentLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Commission Dashboard
            </h1>
            <p className="mt-2 text-muted-foreground">
              Track your earnings from customer transactions
            </p>
          </div>
          <Button onClick={exportHistory} variant="outline" className="gap-2">
            <Download className="h-4 w-4" />
            Export History
          </Button>
        </div>

        {/* Commission Balance Card */}
        <Card className="bg-gradient-to-br from-emerald-600 to-green-600 text-white border-0">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-sm opacity-90 mb-1">Available Commission Balance</p>
                <h2 className="text-4xl font-bold mb-2">
                  {loading ? '...' : `R ${commissionWallet?.balance?.toFixed(2) || '0.00'}`}
                </h2>
                <p className="text-xs opacity-75 mb-4">
                  Total Earned: R {commissionWallet?.total_earned?.toFixed(2) || '0.00'}
                </p>
                <Button 
                  onClick={() => setShowWithdrawModal(true)}
                  disabled={!commissionWallet?.balance || commissionWallet.balance <= 0}
                  className="bg-white text-emerald-700 hover:bg-emerald-50"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Request Withdrawal
                </Button>
              </div>
              <DollarSign className="h-16 w-16 opacity-30" />
            </div>
          </CardContent>
        </Card>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Deposit Commissions</CardTitle>
              <ArrowUpRight className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">R {stats.deposits.commission.toFixed(2)}</div>
              <p className="text-xs text-muted-foreground">{stats.deposits.count} transactions</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Withdrawal Commissions</CardTitle>
              <ArrowUpRight className="h-4 w-4 text-blue-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">R {stats.withdrawals.commission.toFixed(2)}</div>
              <p className="text-xs text-muted-foreground">{stats.withdrawals.count} transactions</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Commission</CardTitle>
              <TrendingUp className="h-4 w-4 text-purple-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">R {stats.total.toFixed(2)}</div>
              <p className="text-xs text-muted-foreground">
                {stats.deposits.count + stats.withdrawals.count} total transactions
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Commission History */}
        <Card>
          <CardHeader>
            <CardTitle>Commission History</CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="all">
              <TabsList>
                <TabsTrigger value="all">All ({commissionHistory.length})</TabsTrigger>
                <TabsTrigger value="deposit">Deposits ({stats.deposits.count})</TabsTrigger>
                <TabsTrigger value="withdrawal">Withdrawals ({stats.withdrawals.count})</TabsTrigger>
              </TabsList>

              <TabsContent value="all" className="mt-4">
                <CommissionHistoryTable history={commissionHistory} />
              </TabsContent>

              <TabsContent value="deposit" className="mt-4">
                <CommissionHistoryTable 
                  history={commissionHistory.filter(h => h.source === 'deposit')} 
                />
              </TabsContent>

              <TabsContent value="withdrawal" className="mt-4">
                <CommissionHistoryTable 
                  history={commissionHistory.filter(h => h.source === 'withdrawal')} 
                />
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>

      {/* Withdrawal Request Modal */}
      {showWithdrawModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center">
                  <Download className="h-5 w-5 mr-2 text-emerald-600" />
                  Request Commission Withdrawal
                </span>
                <Button 
                  size="sm" 
                  variant="ghost" 
                  onClick={() => {
                    setShowWithdrawModal(false);
                    setWithdrawAmount('');
                    setWithdrawNote('');
                  }}
                  className="h-8 w-8 p-0"
                >
                  ✕
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleWithdrawRequest} className="space-y-4">
                <div className="p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg border border-emerald-200 dark:border-emerald-800">
                  <p className="text-sm text-emerald-900 dark:text-emerald-100">
                    <strong>Available:</strong> R {commissionWallet?.balance?.toFixed(2) || '0.00'}
                  </p>
                </div>
                
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    Withdrawal Amount (ZAR)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="1"
                    max={commissionWallet?.balance || 0}
                    placeholder="0.00"
                    value={withdrawAmount}
                    onChange={(e) => setWithdrawAmount(e.target.value)}
                    className="w-full px-3 py-2 border rounded-md"
                    required
                  />
                  <div className="flex justify-between mt-1">
                    <p className="text-xs text-muted-foreground">
                      Min: R 50.00
                    </p>
                    <Button
                      type="button"
                      variant="link"
                      size="sm"
                      className="h-auto p-0 text-xs"
                      onClick={() => setWithdrawAmount(commissionWallet?.balance?.toFixed(2) || '0')}
                    >
                      Withdraw All
                    </Button>
                  </div>
                </div>
                
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    Note (Optional)
                  </label>
                  <textarea
                    rows={3}
                    placeholder="Add any notes for the admin..."
                    value={withdrawNote}
                    onChange={(e) => setWithdrawNote(e.target.value)}
                    className="w-full px-3 py-2 border rounded-md"
                  />
                </div>

                <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                  <p className="text-xs text-blue-900 dark:text-blue-100">
                    💡 Your withdrawal will be reviewed by admin and processed to your registered account within 1-3 business days.
                  </p>
                </div>
                
                <div className="flex gap-2">
                  <Button 
                    type="submit" 
                    className="flex-1"
                    disabled={withdrawing}
                  >
                    {withdrawing ? 'Submitting...' : 'Submit Request'}
                  </Button>
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => {
                      setShowWithdrawModal(false);
                      setWithdrawAmount('');
                      setWithdrawNote('');
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </AgentLayout>
  );
};

const CommissionHistoryTable = ({ history }) => {
  if (history.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <Calendar className="h-12 w-12 mx-auto mb-3 opacity-40" />
        <p>No commission history yet</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {history.map((item, idx) => (
        <div
          key={idx}
          className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-full ${item.source === 'deposit' ? 'bg-green-100 dark:bg-green-900' : 'bg-blue-100 dark:bg-blue-900'}`}>
              <DollarSign className={`h-4 w-4 ${item.source === 'deposit' ? 'text-green-600 dark:text-green-400' : 'text-blue-600 dark:text-blue-400'}`} />
            </div>
            <div>
              <p className="font-medium capitalize">{item.source} Commission</p>
              <p className="text-sm text-muted-foreground">{item.reference}</p>
              <p className="text-xs text-muted-foreground">
                {new Date(item.timestamp).toLocaleString()}
              </p>
            </div>
          </div>
          <div className="text-right">
            <p className="font-semibold text-green-600">+R {item.amount?.toFixed(2)}</p>
            <p className="text-xs text-muted-foreground">Balance: R {item.balance_after?.toFixed(2)}</p>
          </div>
        </div>
      ))}
    </div>
  );
};
